<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import SalesHeader from './SalesHeader.vue';
import SalesFooter from './SalesFooter.vue';
import SettingsPanel from './SettingsPanel.vue';
import CustomerSelectView from './CustomerSelectView.vue';
import DeliveryInfo from './DeliveryInfo.vue';
import ProductSelect from './ProductSelect.vue';
import PaymentSelect from './PaymentSelect.vue';
import OrderSummary from './OrderSummary.vue';
import OrderComplete from './OrderComplete.vue';
import { posStore } from '../../../../stores/posStore';
import { api } from '../../../../api/Api';
import { SalesDocument } from '../../../../types/models';
import { printPDFBlob, sharePDFBlob } from '../../../../utils/CommonFunctions';
import Toast from '../../../../services/Toast';
import { tokenManager } from '../../../../api/utils/tokenManager';

const pos = posStore();
const currentStep = ref(1);
const totalSteps = 5;

const emit = defineEmits(['save']);

const steps = [
  { 
    id: 1, 
    title: 'Select Customer',
    component: CustomerSelectView,
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>`
  },
  { 
    id: 2, 
    title: 'Delivery Information',
    component: DeliveryInfo,
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>`
  },
  { 
    id: 3, 
    title: 'Select Products',
    component: ProductSelect,
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>`
  },
  { 
    id: 4, 
    title: 'Payment Method',
    component: PaymentSelect,
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>`
  },
  { 
    id: 5, 
    title: 'Order Summary',
    component: OrderSummary,
    icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>`
  }
];

const currentStepData = computed(() => {
  return steps.find(step => step.id === currentStep.value) || steps[0];
});

const showHeader = computed(() => currentStep.value <= totalSteps);
const showFooter = computed(() => currentStep.value <= totalSteps);

function goToNextStep() {
  console.log(pos.selectedPosInvoice)
  if (currentStep.value === totalSteps) {
    handleUpdate();
  } else {
    if (currentStep.value == 2 && pos.selectedPosInvoice?.invoice?.to_deliver && !pos.selectedPosInvoice?.invoice?.delivery_address_id) {
      Toast.show('Please select a delivery address before proceeding.', 'warning');
    } else if (currentStep.value == 4 && pos.selectedPosInvoice?.invoice?.payments.length === 0) {
      Toast.show('Please select a payment method before proceeding.', 'warning');
    } else {
      currentStep.value++;
    }
  }
}

const endPoint = "revenue/sales/sales-invoices/";
const handleUpdate = async (
  print = false,
  share = false,
  _delivery = false,
  delivery_staff: string | number = "",
  _thermal = true
) => {
  // Implement your update logic here
  
  let invoice: SalesDocument =
    pos.selectedPosInvoice?.invoice ?? ({} as SalesDocument);
  //invoice.to_deliver = delivery_staff ? true : false;
  invoice.delivery_staffs_id = delivery_staff;
  invoice.share = share || print;

  /*invoice.warehouses_id = pos.selectedPosInvoice?.invoice?.warehouses_id ?? "";
  invoice.price_lists_id = pos.selectedPosInvoice?.invoice?.price_lists_id ?? "";
  invoice.customers_id = pos.selectedPosInvoice?.invoice?.customers_id ?? "";
  invoice.sales_channels_id = pos.selectedPosInvoice?.invoice?.sales_channels_id ?? "";*/

  /*console.log("delivery", delivery);
  invoice.to_deliver = delivery;
  invoice.thermal = thermal;
  invoice.share = share || print;
  invoice.delivery = delivery;
  if (
    !invoice.delivery_address_id &&
    invoice.customer?.addresses &&
    invoice.customer.addresses.length > 0
  ) {
    invoice.delivery_address_id = invoice.customer?.addresses?.[0]?.id ?? "";
  }

  invoice.payments = payments.value;
  invoice.cashier_dutys_id = props.cashierDuty ? props.cashierDuty.id : "";

  invoice.left_margin = props.posStation?.left_margin ?? 0;
  invoice.right_margin = props.posStation?.right_margin ?? 0;
  invoice.top_margin = props.posStation?.top_margin ?? 0;
  invoice.button_margin = props.posStation?.button_margin ?? 0;
  invoice.font_size = props.posStation?.font_size ?? 0;
  invoice.font_bold = props.posStation?.font_bold ?? false;
  invoice.recepit_size = props.posStation?.recepit_size ?? 80;
  */


  const response = await api.post(endPoint + "create", invoice);
  if (response.success == true) {
    emit('save');
    Toast.show("Invoice Created", "info");
    if (print) {
      printPDFBlob(response.data.file)
    }
    if (share) {
      sharePDFBlob(response.lists, "Bill #:" + response.data.data.document_no);
    }
    //printInvoice(response.data, props.posStation);
    startNewSale();
  } else {
    Toast.show("Could not update Invoice", "warning");
  }
  
};

function goToPreviousStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
}

function startNewSale() {
  currentStep.value = 1;
  pos.newInvoice();

  let warehouse = tokenManager.getWarehouse();
    let price_list = tokenManager.getPriceList();
    let sales_channel = tokenManager.getSalesChannel();
  if (pos.selectedPosInvoice && pos.selectedPosInvoice.invoice) {
    if (warehouse) {
      pos.selectedPosInvoice.invoice.warehouses_id = warehouse.id ?? undefined;
      pos.selectedPosInvoice.invoice.warehouse = warehouse.name ?? '';
    }
    if (price_list) {
      pos.selectedPosInvoice.invoice.price_lists_id = price_list.id;
      pos.selectedPosInvoice.invoice.price_list = price_list.name;
    }
    if (sales_channel) {
      pos.selectedPosInvoice.invoice.sales_channels_id = sales_channel.id;
      pos.selectedPosInvoice.invoice.sales_channel = sales_channel.name;
      if (sales_channel.require_delivery) {
        pos.selectedPosInvoice.invoice.to_deliver = true;
      } 
    }
  }
  
}

onMounted(() => {
  // Initialize the POS system or any required data
  pos.newInvoice();
  getPaymentTypes();
});


const getPaymentTypes = async () => {
  const response = await api.post("finance/master-data/payment-types/list");
    if (response.success == true) {
      pos.payment_types = response.data;
    }
};
</script>

<template>
  <div class="mobile-sales-container h-full flex flex-col">
    <SettingsPanel />
    
    <SalesHeader 
      v-if="showHeader" 
      :title="currentStepData.title" 
      :current-step="currentStep" 
      :total-steps="totalSteps"
      :icon="currentStepData.icon"
      @back="goToPreviousStep"
    />
    
    <main class="flex-1 overflow-hidden">
      <transition 
        name="slide" 
        mode="out-in"
      >
        <component 
          :is="currentStep > totalSteps ? OrderComplete : currentStepData.component"
          @next="goToNextStep"
          @new-sale="startNewSale"
        />
      </transition>
    </main>
    
    <SalesFooter 
      v-if="showFooter" 
      :current-step="currentStep" 
      :total-steps="totalSteps"
      @cancel="startNewSale"
      @update="handleUpdate"
      @next="goToNextStep"
    />
  </div>
</template>

<style scoped>
.mobile-sales-container {
  max-width: 768px;
  margin: 0 auto;
  background-color: #ffffff;
  height: 100vh;
  position: relative;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
</style>